import React, { useEffect, useState } from 'react';
import { Button, Dropdown, message, Space, Table, Typography } from 'antd';
import '../index.scss';
import { Loader } from '@/components/layout/DefaultLayout';
import NoData from 'src/assets/images/no-data.png';
import { Franchise, User, UserRole } from '@/types';
import AddExistingUserModal from '@/components/admin/franchisers/franchisors/modals/AddExistingUserModal';
import CreateUserModal from '@/components/admin/franchisers/franchisors/modals/CreateUserModal';
import {
  addExistingFranchisor,
  createFranchisor,
  deleteUser,
  getAllFranchisors,
  updateUser,
} from '@/api/admin';
import queryClient from '@/client/queryClient';
import { useMutation, useQuery } from '@tanstack/react-query';
import { id } from 'date-fns/locale';
import { useParams } from 'react-router';
import { SvgIcon } from '@/components/icon';
import { Link } from 'react-router-dom';
import { format, set } from 'date-fns';
import EditUserModal from './modals/EditUserModal';
import DeleteUserModal from './modals/DeleteUserModal';
import { FormInstance } from 'antd/lib';

interface Props {
  franchisors: User[];
}

interface CreateUserValues {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  [key: string]: any;
}

export default function FranchisorsTable({ franchisors }: Props) {
  const { id: franchiseId } = useParams();
  const [filteredData, setFilteredData] = useState<User[] | undefined>([]);
  const [isCreateUserModalOpen, setIsCreateUserModalOpen] = useState(false);
  const [isAddExistingUserModalOpen, setIsAddExistingUserModalOpen] =
    useState(false);
  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [currentFranchisor, setCurrentFranchisor] =
    useState<Partial<Franchise>>();

  const [childFormInstance, setChildFormInstance] = useState<FormInstance>();

  const domain = window.location.origin;

  useEffect(() => {
    const formattedData = franchisors?.map((franchisor) => ({
      ...franchisor,
      key: franchisor.id,
      name: `${franchisor.firstName} ${franchisor.lastName}`,
    }));

    setFilteredData(formattedData);
  }, [franchisors]);

  const { data: franchisorsOptions } = useQuery<
    | {
        id: string;
        firstName: string;
        lastName: string;
        email: string;
        phone: string;
        franchise: {
          company: string;
        };
      }[]
    | null,
    Error
  >({
    queryKey: ['franchisor', id],
    queryFn: () => getAllFranchisors(),
  });

  const { mutateAsync: createFranchisorMutation, isPending: isCreatingUser } =
    useMutation({
      mutationFn: async (data: {
        firstName: string;
        lastName: string;
        email: string;
        phone: string;
        franchise: string;
        role: UserRole;
      }) => {
        return await createFranchisor({
          ...data,
          role: UserRole.FRANCHISEE,
        });
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['franchisor'],
        });
        message.success('Franchisor created successfully');
      },
      onError: (error: any) => {
        console.error(error);
        const errMsg =
          error?.response?.data?.message ||
          error?.message ||
          'Failed to create franchisor';

        message.error(errMsg);
      },
    });

  const {
    mutateAsync: addExistingUserMutation,
    isPending: isAddingExistingUser,
  } = useMutation({
    mutationFn: async (data: { franchisorId: string; franchiseId: string }) => {
      return await addExistingFranchisor(data.franchisorId, data.franchiseId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['franchisor'],
      });
      message.success('Franchisor added successfully');
    },
  });

  const { mutateAsync: editFranchisorMutation, isPending: isEditingUser } =
    useMutation({
      mutationFn: async (data: Partial<Franchise>) => {
        return await updateUser({
          data,
          id: currentFranchisor?.id || '',
        });
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['franchisor'],
        });
        message.success('Franchisor updated successfully');
        setIsEditUserModalOpen(false);
      },
      onError: (error: any) => {
        console.error(error);
        message.error('Failed to update franchisor: email already exist.');
      },
    });

  const { mutateAsync: deleteFranchisorMutation, isPending: isDeleting } =
    useMutation({
      mutationFn: async (id: string) => {
        return await deleteUser(currentFranchisor?.id || '');
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['franchisor'],
        });
        message.success('Franchisor deleted successfully');
        setIsDeleteModalOpen(false);
      },
    });

  const handleCreateUser = async (values: any) => {
    const trimmedValues: CreateUserValues = Object.fromEntries(
      Object.entries(values).map(([key, value]) => [
        key,
        typeof value === 'string' ? value.trim() : value,
      ]),
    ) as CreateUserValues;
    const requiredFields: (keyof CreateUserValues)[] = [
      'firstName',
      'lastName',
      'email',
      'phone',
    ];
    const emptyFields = requiredFields.filter(
      (field) => !trimmedValues[field] || trimmedValues[field].length === 0,
    );

    if (emptyFields.length > 0) {
      message.error(
        `Please fill in all required fields: ${emptyFields.join(', ')}`,
      );
      return;
    }

    await createFranchisorMutation({
      ...values,
      franchise: franchiseId,
    });
    setIsCreateUserModalOpen(false);
    childFormInstance?.resetFields();
  };

  const handleAddExistingUser = async (values: any) => {
    if (!franchiseId) return;
    await addExistingUserMutation({
      franchisorId: values.user,
      franchiseId,
    });
    setIsAddExistingUserModalOpen(false);
  };

  const handleFranchisorEdit = async (franchisor: any) => {
    setIsEditUserModalOpen(true);
    setCurrentFranchisor(franchisor);
  };

  const handleFranchisorDelete = async (franchisor: any) => {
    setIsDeleteModalOpen(true);
    setCurrentFranchisor(franchisor);
  };

  const columns = [
    {
      title: 'User',
      dataIndex: 'fullName',
      key: 'fullName',
      render: (_: any, franchisor: any) =>
        `${franchisor.firstName} ${franchisor.lastName}`,
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: 'SMS Consent',
      dataIndex: 'smsConsent',
      key: 'smsConsent',
      render: (value: boolean | undefined) => (
        <Typography.Text>
          {value === undefined ? 'No' : value ? 'Yes' : 'No'}
        </Typography.Text>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 300, // Set fixed width for the column
      render: (email: string) => (
        <Typography.Text
          style={{
            display: 'block',
            wordBreak: 'break-word',
            whiteSpace: 'normal',
            lineHeight: '1.4',
          }}
        >
          {email}
        </Typography.Text>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'isVerified',
      key: 'isVerified',
      render: (status: string, franchisor: User) => {
        let statusColor = '#1C8271';
        let statusText = 'Activated';
        if (status) {
          statusColor = '#1C8271';
          statusText = 'Activated';
        }
        if (!franchisor.status) {
          statusColor = '#F6C38B';
          statusText = 'Hidden';
        }
        if (franchisor.isDeleted) {
          statusColor = '#F46262';
          statusText = 'Deleted';
        }
        if (franchisor.isDeactivated) {
          statusColor = '#2A4BC2';
          statusText = 'Deactivated';
        }
        if (!status) {
          statusColor = '#F68B8B';
          statusText = 'Inactive';
        }
        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: statusColor,
              }}
            />
            <Typography.Text>{statusText}</Typography.Text>
          </div>
        );
      },
    },
    {
      title: 'Activation Link',
      dataIndex: 'tokenActivation',
      key: 'tokenActivation',
      render: (tokenActivation: string) =>
        tokenActivation ? (
          <Link
            style={{ fontWeight: 500, color: '#1C8271' }}
            to={`${domain}/activate-profile-franchisor?token=${tokenActivation}`}
            target={'_blank'}
            rel={'noopener noreferrer'}
          >
            Activate
          </Link>
        ) : (
          `N/A`
        ),
    },
    {
      title: 'Token Expiration',
      dataIndex: 'tokenActivationExpiresAt',
      key: 'tokenActivationExpiresAt',
      render: (tokenActivationExpiresAt: string) =>
        tokenActivationExpiresAt ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <Typography.Text>
              {format(
                new Date(tokenActivationExpiresAt),
                'd MMM, yyyy, h:mm a',
              )}
            </Typography.Text>
          </div>
        ) : (
          `N/A`
        ),
    },
    {
      title: '',
      dataIndex: 'actions',
      key: 'actions',
      render: (_: any, franchisor: any) => {
        const menu = {
          items: [
            {
              key: 'edit',
              label: 'Edit',
              onClick: () => handleFranchisorEdit(franchisor),
            },
            {
              key: 'delete',
              label: 'Delete',
              danger: true,
              onClick: () => handleFranchisorDelete(franchisor),
            },
          ],
        };

        return (
          <Dropdown menu={menu}>
            <Button type={'link'}>
              <Space>
                <SvgIcon type={'threeDots'} width={18} height={18} />
              </Space>
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  if (!filteredData) return <Loader />;

  return (
    <React.Fragment>
      <div className={'tables-wrapper'}>
        {/* Actions Section (Search and Pagination Controls) */}
        <div className={'tables-actions'} style={{ margin: '16px' }}>
          <Space
            direction={'horizontal'}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div className={'tables-actions-search'}>
              {/* Naming */}
              <Typography.Title
                level={3}
                style={{ textWrap: 'nowrap', marginBlock: 8 }}
              >
                Franchisors
              </Typography.Title>
            </div>
            <div className={'tables-actions-search'}>
              <Button
                type={'default'}
                className={'admin-button-download-table'}
                onClick={() => setIsAddExistingUserModalOpen(true)}
              >
                Add Existing Franchisor
              </Button>
              <Button
                type={'default'}
                className={'admin-button-download-table'}
                onClick={() => setIsCreateUserModalOpen(true)}
              >
                Create New Franchisor
              </Button>
            </div>
          </Space>
        </div>

        {filteredData.length === 0 ? (
          <div className={'no-data-wrapper'} style={{ height: 0 }}>
            <img src={NoData} alt={'No Data'} />
            <Typography.Text
              style={{
                color: '#9FA1AD',
                fontWeight: 500,
                maxWidth: '200px',
                textAlign: 'center',
              }}
            >
              {`It looks like there aren't any franchisors in this franchise.`}
            </Typography.Text>
          </div>
        ) : (
          <Table
            dataSource={filteredData}
            columns={columns}
            pagination={false}
            style={{ marginTop: '0' }}
          />
        )}
      </div>
      <CreateUserModal
        childFormInstance={setChildFormInstance}
        open={isCreateUserModalOpen}
        onCancel={() => setIsCreateUserModalOpen(false)}
        handleFinish={handleCreateUser}
        loading={isCreatingUser}
      />
      <AddExistingUserModal
        franchisors={franchisorsOptions || []}
        open={isAddExistingUserModalOpen}
        onCancel={() => setIsAddExistingUserModalOpen(false)}
        handleFinish={handleAddExistingUser}
        loading={isAddingExistingUser}
      />
      <EditUserModal
        open={isEditUserModalOpen}
        onCancel={() => setIsEditUserModalOpen(false)}
        handleFinish={editFranchisorMutation}
        loading={isEditingUser}
        currentFranchisor={currentFranchisor}
      />
      <DeleteUserModal
        open={isDeleteModalOpen}
        onCancel={() => setIsDeleteModalOpen(false)}
        handleFinish={deleteFranchisorMutation}
        loading={isDeleting}
        currentFranchisor={currentFranchisor}
      />
    </React.Fragment>
  );
}
